import { z } from 'zod';
import { IsoDateTime, Uuid } from '../primitives.js';

/**
 * WS-13 Feature 5 — BEFORE/AFTER PHOTOS (LRU-15).
 *
 * DESIGN (LOCKED, WS13-retention-design.md §Feature 5 + Locked decision 2): a PII/CONSENT feature that is
 * STORAGE-GATED (inert). The IMAGE BYTES live in an S3-compatible object bucket behind a StoragePort
 * (integrations/storage/) with a fake/inert mode — uploads return 503 ('photo storage not configured')
 * until a real bucket is provisioned (the same posture as WS-8 deposits / WS-10 billing). The METADATA
 * lives in an RLS-protected tenant table (client_photos); the bytes are NEVER in Postgres (never bytea,
 * never local-disk-behind-static — that would bypass RLS).
 *
 * FAIL-CLOSED visibility ladder (these are identifiable client PII images):
 *   • salon_private (DEFAULT) — only the salon's staff who can see the client.
 *   • client_visible          — promotable by ANY staff who can see the client; the client sees it in the portal.
 *   • showcase                — public/marketing likeness; promotable ONLY with consent_marketing = true AND a
 *                               manager/owner action. Distinct from clients.marketingConsent (messaging consent).
 *
 * Signed download URLs are SHORT-LIVED and minted ONLY after a withTenant/identity check confirms the caller
 * may see this client's photos — the bucket key is never a standalone capability. pair_id groups the
 * before+after of one SET; LRU-15 caps 15 sets per (salon, client), evicting the oldest set + its blobs on
 * the 16th. Client PII images are erasable: GDPR client-erase enqueues a blob delete per storage/thumb key.
 */

// ── Enums ────────────────────────────────────────────────────────────────────

/** before | after — the two halves of one comparison SET (grouped by pairId). */
export const PhotoKind = z.enum(['before', 'after']);
export type PhotoKind = z.infer<typeof PhotoKind>;

/** pending (upload not yet confirmed) → ready (bytes confirmed; the gallery mints a download URL). */
export const PhotoStatus = z.enum(['pending', 'ready']);
export type PhotoStatus = z.infer<typeof PhotoStatus>;

/**
 * The fail-closed visibility ladder. 'salon_private' (DEFAULT) — staff only. 'client_visible' — the client
 * sees it in the portal. 'showcase' — public/marketing likeness (needs consent_marketing + a manager).
 */
export const PhotoVisibility = z.enum(['salon_private', 'client_visible', 'showcase']);
export type PhotoVisibility = z.infer<typeof PhotoVisibility>;

// ── Entity (operator/staff view — internal ids present; this is the authed surface) ─────────────

/**
 * One stored photo's METADATA (the authed/staff view). The bytes live in the bucket; `storageKey`/`thumbKey`
 * are opaque paths. `downloadUrl` is a freshly-minted SHORT-LIVED signed GET URL, present ONLY on a 'ready'
 * photo read through the gallery (after the tenant check) — it is NOT a stored column. The staff surface may
 * carry internal ids (it is already tenant-gated); the CLIENT-portal DTO below strips them.
 */
export const ClientPhoto = z
  .object({
    id: Uuid,
    salonId: Uuid,
    renterId: Uuid.nullable(),
    clientId: Uuid,
    appointmentId: Uuid.nullable(),
    pairId: Uuid,
    kind: PhotoKind,
    storageKey: z.string().describe('opaque bucket path — never a standalone capability'),
    thumbKey: z.string().nullable(),
    contentType: z.string(),
    byteSize: z.int().min(0).nullable(),
    width: z.int().min(1).nullable(),
    height: z.int().min(1).nullable(),
    status: PhotoStatus,
    visibility: PhotoVisibility,
    consentMarketing: z.boolean(),
    consentCapturedAt: IsoDateTime.nullable(),
    consentCapturedByStaffId: Uuid.nullable(),
    uploadedByStaffId: Uuid.nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    /** A freshly-minted short-lived signed GET URL — present only on a ready photo read via the gallery. */
    downloadUrl: z
      .string()
      .nullable()
      .describe('minted only after the tenant read confirms access'),
  })
  .meta({ id: 'ClientPhoto' });
export type ClientPhoto = z.infer<typeof ClientPhoto>;

// ── Upload initiate (presign) ──────────────────────────────────────────────────

/**
 * Initiate an upload: validate the content-type (image/*) + a sane size cap, mint a storage_key, INSERT a
 * 'pending' metadata row (visibility defaults salon_private), and return the presigned PUT URL + the photo id.
 * If storage is disabled (no bucket creds on staging) the route returns 503 — an honest disabled UX, no broken
 * presigned URL. `pairId` is optional: omitted = a NEW set (a fresh pair_id is minted); supplied = attach to an
 * existing set (e.g. the 'after' of a 'before').
 */
export const InitiatePhotoUpload = z
  .object({
    contentType: z.string().describe('the image MIME type — must be image/*'),
    /** A client-declared byte size hint, validated against the server cap (the real size is set at confirm). */
    byteSize: z.int().min(1).optional(),
    kind: PhotoKind.default('before'),
    appointmentId: Uuid.optional().describe('optional attach-to-visit'),
    pairId: Uuid.optional().describe(
      'omit = a new set; supply = attach to an existing before/after set',
    ),
  })
  .meta({ id: 'InitiatePhotoUpload' });
export type InitiatePhotoUpload = z.infer<typeof InitiatePhotoUpload>;

/**
 * The presign-init result: the photo id (a 'pending' metadata row) + the presigned PUT URL the client uploads
 * the bytes to, plus any extra form fields the bucket requires (POST-policy uploads). After the bytes land the
 * client calls POST /v1/photos/:id/confirm to flip status → ready. `evicted` lists any SETS the LRU-15 cap
 * dropped when this upload pushed the (salon, client) over 15 sets (no silent unbounded growth).
 */
export const PhotoUploadTicket = z
  .object({
    id: Uuid,
    pairId: Uuid,
    uploadUrl: z.string(),
    fields: z
      .record(z.string(), z.string())
      .nullable()
      .describe('extra form fields for a POST-policy upload'),
    storageKey: z.string(),
    /** The pair_ids of any SETS evicted by the LRU-15 cap when this initiate ran (their blobs were enqueued for delete). */
    evictedPairIds: z.array(Uuid),
  })
  .meta({ id: 'PhotoUploadTicket' });
export type PhotoUploadTicket = z.infer<typeof PhotoUploadTicket>;

/**
 * Confirm an upload completed: flip status pending → ready and record the real dims/byte_size. Thumbnailing is
 * deferred (finalised with the storage backend) — thumbKey stays null today.
 */
export const ConfirmPhotoUpload = z
  .object({
    byteSize: z.int().min(0).optional(),
    width: z.int().min(1).optional(),
    height: z.int().min(1).optional(),
  })
  .meta({ id: 'ConfirmPhotoUpload' });
export type ConfirmPhotoUpload = z.infer<typeof ConfirmPhotoUpload>;

/**
 * Patch a photo's grouping/visibility: set kind (before|after), re-pair (pairId), or move along the visibility
 * ladder. Promotion to 'client_visible' is allowed for any staff who can see the client; promotion to
 * 'showcase' requires manager/owner AND consent_marketing already recorded (the route 409/403s otherwise).
 */
export const UpdatePhoto = z
  .object({
    kind: PhotoKind.optional(),
    pairId: Uuid.optional(),
    visibility: PhotoVisibility.optional(),
  })
  .meta({ id: 'UpdatePhoto' });
export type UpdatePhoto = z.infer<typeof UpdatePhoto>;

// ── Client-portal DTO (NO internal ids — customer-facing only) ──────────────────

/**
 * The client-portal view of ONE of the client's own photos (within a set). Customer-facing ONLY: NO internal
 * ids (photo id / clientId / renterId / staffId / storageKey). The `downloadUrl` is a freshly-minted
 * short-lived signed GET URL, minted only after the claimed-salon identity check confirms ownership.
 */
export const ClientPhotoView = z
  .object({
    salonName: z.string(),
    kind: PhotoKind,
    visibility: PhotoVisibility,
    downloadUrl: z.string(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'ClientPhotoView' });
export type ClientPhotoView = z.infer<typeof ClientPhotoView>;

/**
 * One SET (the before+after of one pair_id) the client may see in the portal — only sets with at least one
 * client_visible/showcase ready photo. Customer-facing ONLY: NO pair_id or internal ids leave the surface.
 */
export const ClientPhotoSet = z
  .object({
    salonName: z.string(),
    createdAt: IsoDateTime,
    photos: z.array(ClientPhotoView),
  })
  .meta({ id: 'ClientPhotoSet' });
export type ClientPhotoSet = z.infer<typeof ClientPhotoSet>;

/**
 * A public showcase item (GET /public/showcase/:salonSlug): a single showcase photo with consent recorded.
 * NO internal ids, NO client identity — just the salon's public portfolio likeness with a signed URL.
 */
export const ShowcasePhoto = z
  .object({
    kind: PhotoKind,
    downloadUrl: z.string(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'ShowcasePhoto' });
export type ShowcasePhoto = z.infer<typeof ShowcasePhoto>;
